
import { useCallback } from 'react';
import { 
  handleError, 
  ErrorSeverity, 
  ErrorContext, 
  ErrorCategory
} from '@/utils/errorHandler';

/**
 * Custom hook that provides component-specific error handling utilities
 * 
 * @param componentName The name of the component using this hook
 * @returns Object containing error handling functions
 */
export const useErrorHandler = (componentName: string) => {
  const handleComponentError = useCallback(
    (
      error: unknown,
      operation: string,
      additionalInfo: Record<string, any> = {},
      showToast: boolean = true
    ) => {
      return handleError(
        error,
        `Error in ${componentName}: ${operation}`,
        ErrorSeverity.ERROR,
        {
          source: componentName,
          operation,
          additionalInfo
        },
        showToast
      );
    },
    [componentName]
  );

  const wrapAsync = useCallback(
    async <T>(
      promise: Promise<T>,
      operation: string,
      additionalInfo: Record<string, any> = {},
      showToast: boolean = true
    ): Promise<[T | null, Error | null]> => {
      try {
        const data = await promise;
        return [data, null];
      } catch (error) {
        const handledError = handleComponentError(
          error,
          operation,
          additionalInfo,
          showToast
        );
        return [null, handledError];
      }
    },
    [handleComponentError]
  );

  return {
    handleError: handleComponentError,
    wrapAsync,
    ErrorSeverity,
    ErrorCategory
  };
};

export default useErrorHandler;
